import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useSearchParams, useLocation } from 'react-router-dom';
import KanbanBoard from './Kanban/KanbanBoard';
import MapaLeadsView from './Mapa/MapaLeadsView';
import ProspeccaoView from './Prospeccao/ProspeccaoView';
import RotasView from './Rotas/RotasView';
import OperadorView from './Rotas/OperadorView';
import AnalyticsComercialView from './Analytics/AnalyticsComercialView';
import AnaliseConversasView from './AnaliseConversas/AnaliseConversasView';
import PosVendaView from './PosVenda/PosVendaView';
import IndicacoesView from './Indicacoes/IndicacoesView';
import LemitView from '../Gestao/LemitView';
import MinhaComissaoView from './MinhaComissao/MinhaComissaoView';
import RemarketingView from './Remarketing/RemarketingView';
import { podeTrabalharRemarketing } from '../../src/lib/remarketing';
import NegociacaoFAB from './components/NegociacaoFAB';
import NegotiationWizard from './components/NewNegotiationWizard/NegotiationWizard';
import { UCData } from './components/NewNegotiationWizard/StepUC';
import NegotiationDetailModal from './components/NegotiationDetails/NegotiationDetailModal';
import { useAuth } from '../../src/contexts/AuthContext';
import { MOSTRAR_ITENS_V2_EM_DISCUSSAO } from '../../src/lib/politicaComercial';
import { GERAR_ROTA_TERCEIROS_PERMISSION } from './Rotas/rotaTypes';
import { avisarKanban, gravarFunilAtivo } from '../../src/lib/pipelines';

const CRMLayout: React.FC = () => {
  const { profile } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [createdNegociacaoId, setCreatedNegociacaoId] = useState<number | null>(null);
  const [wizardPrefill, setWizardPrefill] = useState<{
    nome_cliente?: string;
    cpf_cnpj?: string;
    tipoCliente?: 'PF' | 'PJ';
    associateUC?: boolean;
    ucsPrefill?: Partial<UCData>[];
    faturaFiles?: { file: File; label?: string }[];
    documentoFile?: File;
    documentoLabel?: string;
  } | undefined>(undefined);

  // Atalho do menu lateral mobile (?novaNegociacao=1) — abre o wizard direto ao chegar no CRM
  useEffect(() => {
    if (searchParams.get('novaNegociacao') === '1') {
      setWizardPrefill(undefined);
      setWizardOpen(true);
      searchParams.delete('novaNegociacao');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Prospecção Geo consome API paga — restrita a admin/gerente comercial ou
  // quem tem a permissão especial "Fazer Rotas" (Config > Permissões Especiais)
  const canProspect = profile?.role === 'adm' || profile?.role === 'gerente'
    || !!profile?.permissions?.includes(GERAR_ROTA_TERCEIROS_PERMISSION);
  // Gestor comercial: atribui visita de pós-venda a qualquer vendedor do time
  const isGestorComercial = profile?.role === 'adm' || profile?.role === 'gerente';
  // Motoboy (operador de campo): apenas execução de rotas — sem Kanban, Mapa, Analytics ou criação de negociações
  const isMotoboy = profile?.role === 'motoboy';
  // Parceiro/vendedor parceiro: CRM simplificado — sem Mapa Inteligente (Analytics continua disponível)
  const isParceiroRole = profile?.role === 'parceiro' || profile?.role === 'vendedor_parceiro';
  // O FAB flutua sobre o conteúdo e atrapalha a Prospecção (tabela densa + paginação
  // no rodapé). Lá a criação de lead é pela própria importação, então some com ele.
  const ocultarFAB = location.pathname.startsWith('/crm/prospeccao');

  return (
    <div className="space-y-4">
      <Routes>
        {/* Index: Kanban (motoboy vai direto p/ Rotas) */}
        <Route index element={isMotoboy ? <Navigate to="/crm/rotas" replace /> : <KanbanBoard />} />
        <Route path="mapa" element={(isMotoboy || isParceiroRole) ? <Navigate to="/crm" replace /> : <MapaLeadsView />} />
        <Route path="prospeccao" element={<ProspeccaoView />} />
        {/* Central Lemit para o Comercial (Closer, BDR, Hunter e gerência): a mesma tela da Gestão,
            que confere o acesso sozinha (hasLemitAccess). Hunter e BDR têm papel vendedor e não entram na Gestão. */}
        <Route path="lemit" element={<LemitView />} />
        <Route path="rotas" element={isMotoboy ? <OperadorView /> : <RotasView />} />
        {/* Central de Sugestões de pós-venda: só gestor atribui (spec pós-venda §10).
            O RoleGuard de /crm/* em App.tsx só barra empresa_parceira_*, então a
            restrição a adm/gerente é aqui — mesmo padrão de minha-comissao abaixo. */}
        <Route path="pos-venda" element={isGestorComercial ? <PosVendaView /> : <Navigate to="/crm" replace />} />
        {/* Remarketing de perdidos (#6419): BDR, gerente e adm. A RLS repete a regra. */}
        <Route path="remarketing" element={podeTrabalharRemarketing(profile) ? <RemarketingView /> : <Navigate to="/crm" replace />} />
        {/* Programa de Indicações (#6196). A própria tela redireciona quem não usa o programa. */}
        <Route path="indicacoes" element={<IndicacoesView />} />
        <Route path="analytics" element={isMotoboy ? <Navigate to="/crm" replace /> : <AnalyticsComercialView />} />
        {/* Quem vê o quê é a RLS: adm/gerente tudo, a atendente só o dela (#6313). */}
        <Route path="analise-conversas" element={<AnaliseConversasView />} />
        {/* Minha Comissão: só vendedor externo (a edge function também restringe) */}
        <Route path="minha-comissao" element={MOSTRAR_ITENS_V2_EM_DISCUSSAO && profile?.role === 'vendedor' ? <MinhaComissaoView /> : <Navigate to="/crm" replace />} />
      </Routes>

      {!isMotoboy && (
        <>
          {!ocultarFAB && <NegociacaoFAB
            onInsertData={() => {
              setWizardPrefill(undefined);
              setWizardOpen(true);
            }}
            onAutomaticoReady={(result) => {
              setWizardPrefill({
                nome_cliente: result.nome_cliente,
                cpf_cnpj: result.cpf_cnpj,
                tipoCliente: result.tipoCliente,
                associateUC: result.faturas.length > 0,
                ucsPrefill: result.faturas.map(f => f.uc),
                faturaFiles: result.faturas.map(f => ({
                  file: f.file,
                  label: f.uc.numero_uc ? `Fatura - UC ${f.uc.numero_uc}` : undefined,
                })),
                documentoFile: result.documentoFile,
                documentoLabel: result.documentoLabel,
              });
              setWizardOpen(true);
            }}
          />}

          <NegotiationWizard
            isOpen={wizardOpen}
            onClose={() => setWizardOpen(false)}
            onSuccess={(negociacaoId, pipelineId) => {
              // As DUAS coisas, de propósito: o FAB também existe em /crm/mapa,
              // /crm/rotas e /crm/analytics, onde o KanbanBoard está desmontado e o
              // evento se perde. O localStorage garante que ele monte no funil certo
              // depois; o evento resolve quando o quadro já está na tela.
              gravarFunilAtivo(profile?.id, pipelineId);
              avisarKanban(pipelineId);
              setCreatedNegociacaoId(negociacaoId);
            }}
            prefillData={wizardPrefill}
          />

          <NegotiationDetailModal
            isOpen={createdNegociacaoId !== null}
            onClose={() => setCreatedNegociacaoId(null)}
            negotiationId={createdNegociacaoId}
            onUpdate={() => window.dispatchEvent(new CustomEvent('refreshKanban'))}
          />
        </>
      )}
    </div>
  );
};

export default CRMLayout;
